import { Button, Stack } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { VailAccordion, VailSelect, VailTextField } from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType, CreateCustomComponentInput } from '@/utils/api'

export const SettingsTab = () => {
  const form = useForm<CreateCustomComponentInput>({
    defaultValues: {
      type: ComponentType.recipe,
    },
  })
  const navigate = useNavigate()
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const createRecipe = useCreateCustomComponent(orgID)
  const currentRecipe = useGetCustomComponent(componentID)
  const updateRecipe = useUpdateCustomComponent(componentID)
  const { reset } = form

  useEffect(() => {
    if (!currentRecipe.data) return
    const { name, description } = currentRecipe.data
    reset({ description, name })
  }, [currentRecipe.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID)
      return await updateRecipe.mutateAsync({
        ...data,
        id: componentID,
        type: ComponentType.recipe,
      })
    const res = await createRecipe.mutateAsync({
      ...data,
      type: ComponentType.recipe,
    })
    return navigate(`../${res?.id}`)
  })
  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save_recipe_config')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              control={form.control}
              defaultValue={ComponentType.recipe}
              label="Type"
              name="type"
              options={[
                {
                  label: 'Recipe',
                  value: ComponentType.recipe,
                },
              ]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
